import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { ShieldCheck, Wallet, AlertTriangle } from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  ChartCard,
  tooltipStyle,
  CHART_COLORS,
  fmt,
  LoadingPanel,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { countBy, perspectiveSnapshot } from "@/lib/analytics";
import { leadRca, perspectiveConfig } from "@/lib/perspective";

export const Route = createFileRoute("/payments")({
  head: () => ({ meta: [{ title: "Payments — Ready4Trade" }] }),
  component: PaymentsPage,
});

function PaymentsPage() {
  const { leads, aggregates, isLoading, perspective } = useTradeData();
  const config = perspectiveConfig(perspective);
  const snapshot = useMemo(
    () => (leads ? perspectiveSnapshot(leads, perspective) : null),
    [leads, perspective],
  );
  const allPayment = useMemo(
    () =>
      leads
        ? countBy(
            leads.map((lead) => lead.pay || "Not specified"),
            12,
          )
        : [],
    [leads],
  );
  const comparison = useMemo(() => {
    if (!snapshot) return [];
    const names = new Set([
      ...allPayment.slice(0, 10).map((row) => row.name),
      ...snapshot.paymentAligned.slice(0, 10).map((row) => row.name),
    ]);
    return [...names]
      .map((name) => ({
        name,
        "All leads": allPayment.find((row) => row.name === name)?.count ?? 0,
        "RCA > 1": snapshot.paymentAligned.find((row) => row.name === name)?.count ?? 0,
      }))
      .sort((a, b) => b["RCA > 1"] - a["RCA > 1"] || b["All leads"] - a["All leads"])
      .slice(0, 10);
  }, [allPayment, snapshot]);
  const alignedCount = useMemo(
    () => leads?.filter((lead) => (leadRca(lead, perspective) ?? 0) > 1 && lead.pay).length ?? 0,
    [leads, perspective],
  );

  if (isLoading || !leads || !aggregates || !snapshot)
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );

  return (
    <DashboardShell meta={aggregates.meta}>
      <PageHeading
        title={`${config.flag} Payment intelligence`}
        subtitle="Compare all buyer payment preferences with the subset of products where the selected exporter has pooled RCA above 1."
        badge={`${fmt(alignedCount)} RCA-aligned leads with terms`}
      />

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="RCA-aligned payment mix"
          subtitle="Most common payment labels in the selected opportunity set"
          icon={<Wallet className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={420}>
            <PieChart>
              <Pie
                data={snapshot.paymentAligned.slice(0, 8)}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="44%"
                innerRadius={72}
                outerRadius={125}
                paddingAngle={2}
              >
                {snapshot.paymentAligned.slice(0, 8).map((_, index) => (
                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="All leads versus RCA-aligned"
          subtitle="Does the commercially relevant subset have a different payment profile?"
          icon={<ShieldCheck className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={comparison} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                dataKey="name"
                type="category"
                width={165}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
              <Legend />
              <Bar dataKey="All leads" fill="#cbd5e1" radius={[0, 5, 5, 0]} />
              <Bar dataKey="RCA > 1" fill="#10b981" radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <section className="surface-panel p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h3 className="font-display text-base">Exporter risk interpretation</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr className="text-left">
                <Th>Payment term</Th>
                <Th className="text-right">RCA-aligned leads</Th>
                <Th>Practical exporter view</Th>
              </tr>
            </thead>
            <tbody>
              {snapshot.paymentAligned.map((item) => (
                <tr key={item.name} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{item.name}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(item.count)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{explain(item.name)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Always verify the buyer, issuing bank, documentary conditions, sanctions exposure and the
          exporter country's foreign-exchange requirements before accepting terms.
        </p>
      </section>
    </DashboardShell>
  );
}

function explain(term: string): string {
  const upper = term.toUpperCase();
  if (upper.includes("L/C") || upper === "LC" || upper.includes("DLC"))
    return "Potentially lower payment risk when issued or confirmed by an acceptable bank; examine every documentary condition.";
  if (upper.includes("T/T") || upper === "TT" || upper.includes("BANK TRANSFER"))
    return "Fast and simple, but exporter protection depends on the advance-payment percentage and buyer reliability.";
  if (upper.includes("CAD") || upper.includes("DOCUMENT"))
    return "Documents are released against payment; risk remains if the buyer refuses documents or goods.";
  if (upper.includes("DISCUSS") || upper.includes("NEGOT"))
    return "Use the first negotiation to propose a deposit, secure instrument and clear payment milestones.";
  return "Clarify timing, currency, bank charges and documentary requirements before issuing a pro-forma invoice.";
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-[11px] uppercase font-semibold tracking-wider ${className}`}>
      {children}
    </th>
  );
}
